/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  EmptyState,
  PageHead,
  Banner,
  DashboardTopPortal,
  Button,
  AddAssetDropdown,
  SVG,
} from "shared";
import { SVG_ENUM, PACKET_ENUM, AssetCategorySlug } from "enums";
import { isDesktop } from "utils";
import "./InvestmentsPage.scss";
import InvestmentsTopBar from "./components/InvestmentsTopBar";
import InvestmentsTabs from "./components/InvestmentsTabs";
import AddInvestmentModal from "./components/AddInvestmentModal/AddInvestmentModal";
import AssetTilesList from "./components/AssetTilesList/AssetTilesList";
import { useInvestmentsState } from "./hooks/useInvestmentsState";
import {
  useGetInvestmentsGrouped,
  useGetInvestmentsAdminClients,
} from "hooks/investments";
import { GroupedInvestmentsData, CompaniesTabsType } from "./types";
import InvestmentClientFilter, {
  InvestmentClientOption,
} from "./components/InvestmentClientFilter/InvestmentClientFilter";
import {
  AddCompanyModal,
  AddCompanyClientModal,
  SellSharesModal,
} from "features/companies";
import TransactionsSideModal from "features/companies/TransactionsSideModal/TransactionsSideModal";
import { AddSharesModal } from "features/shares";
import { PacketModal } from "features/users";
import InvestmentsSummary from "./components/InvestmentsSummary/InvestmentsSummary";
import {
  openMeetingBooking,
  filterCategoriesByClient,
  filterCategoriesByType,
  sortCategoriesBySlugOrder,
  getTypeBadges,
  getClientUuidsForType,
} from "./utils";

const InvestmentsPage = () => {
  const queryClient = useQueryClient();
  const transactionCompanyRef = useRef<any>(null);
  const [transactionCompany, setTransactionCompany] = useState<any>(null);
  const [sellSharesCompany, setSellSharesCompany] = useState<any>(null);
  const [addSharesCompany, setAddSharesCompany] = useState<any>(null);
  const [pendingOpenUuid, setPendingOpenUuid] = useState<string | null>(null);
  const [pendingOpenIsOwn, setPendingOpenIsOwn] = useState<boolean | null>(
    null,
  );
  const [pendingOpenCurrencyUuid, setPendingOpenCurrencyUuid] = useState<
    string | null
  >(null);
  const [pendingArchiveUuid, setPendingArchiveUuid] = useState<string | null>(
    null,
  );

  const [pendingRedirect, setPendingRedirect] = useState<{
    uuid: string;
    target: "archive" | "my";
    capturedAt: number;
    isOwn?: boolean;
  } | null>(null);

  const returnToTransactionSideModal = (data?: any) => {
    if (data?.availableShares === 0) {
      const uuid =
        transactionCompanyRef.current?.companyUuid ??
        transactionCompanyRef.current?.uuid;
      setSellSharesCompany(null);
      setAddSharesCompany(null);
      setTransactionCompany(null);
      if (uuid) {
        handleSellAllSuccess(uuid);
      }
      return;
    }
    if (data?.availableShares !== undefined) {
      transactionCompanyRef.current = {
        ...transactionCompanyRef.current,
        sharesCount: data.availableShares,
        availableShares: data.availableShares,
      };
    }
    setSellSharesCompany(null);
    setAddSharesCompany(null);
    setTimeout(
      () => setTransactionCompany({ ...transactionCompanyRef.current }),
      100,
    );
  };

  const {
    tab,
    isAdmin,
    isCompanyInfoOpenModal,
    authorizedUser,
    modalManage,
    setModalManage,
    selectedSlug,
    setAddAsset,
    closeAddAssetModal,
    handleChangeTab: handleChangeTabInternal,
    // page,
  } = useInvestmentsState();

  const isRelianceTab = tab === "reliance";
  const isAdminOtherTab = isAdmin && tab === "other";

  const [selectedAdminClient, setSelectedAdminClient] =
    useState<InvestmentClientOption | null>(null);
  const [selectedTypeSlug, setSelectedTypeSlug] = useState<string | null>(null);

  const handleChangeTab = (item: CompaniesTabsType["tab"]) => {
    if (selectedAdminClient && (item === "reliance" || item === "other")) {
      const targetData =
        item === "reliance"
          ? typedGroupedData?.reliance
          : typedAllUsersGroupedData?.reliance;
      const filtered = filterCategoriesByClient(
        targetData,
        selectedAdminClient.value,
      );
      const hasItems = filtered && Object.keys(filtered).length > 0;
      if (!hasItems) {
        setSelectedAdminClient(null);
      }
    } else {
      setSelectedAdminClient(null);
    }
    setSelectedTypeSlug(null);
    handleChangeTabInternal(item);
  };

  const adminClientsTab = isAdminOtherTab ? "other" : "reliance";
  const { data: clientsOptions } = useGetInvestmentsAdminClients(
    adminClientsTab,
    isAdmin && (isRelianceTab || isAdminOtherTab),
  );

  const {
    data: groupedInvestments,
    isLoading: isGroupedLoading,
    isFetching: isGroupedFetching,
    dataUpdatedAt: groupedUpdatedAt,
    refetch: refetchGrouped,
  } = useGetInvestmentsGrouped(
    !isAdmin && isRelianceTab ? { tab: "reliance" } : undefined,
  );

  const { data: groupedAllUsers, isLoading: isAllUsersGroupedLoading } =
    useGetInvestmentsGrouped({ allUsers: true, enabled: isAdminOtherTab });

  const {
    data: archivedInvestments,
    isLoading: isArchivedLoading,
    isFetching: isArchivedFetching,
    dataUpdatedAt: archivedUpdatedAt,
  } = useGetInvestmentsGrouped(true);

  const typedGroupedData = groupedInvestments as
    | GroupedInvestmentsData
    | undefined;

  const typedAllUsersGroupedData = groupedAllUsers as
    | GroupedInvestmentsData
    | undefined;

  const typedArchivedData = archivedInvestments as
    | GroupedInvestmentsData
    | undefined;

  const clientFilteredReliance = filterCategoriesByClient(
    typedGroupedData?.reliance,
    selectedAdminClient?.value,
  );
  const filteredRelianceCategories = sortCategoriesBySlugOrder(
    filterCategoriesByType(clientFilteredReliance, selectedTypeSlug),
  );

  const clientFilteredOther = filterCategoriesByClient(
    typedAllUsersGroupedData?.reliance,
    selectedAdminClient?.value,
  );
  const filteredOtherCategories = sortCategoriesBySlugOrder(
    filterCategoriesByType(clientFilteredOther, selectedTypeSlug),
  );

  const relianceBadges = getTypeBadges(clientFilteredReliance);
  const otherBadges = getTypeBadges(clientFilteredOther);

  const relianceClientUuidsForType = getClientUuidsForType(
    typedGroupedData?.reliance,
    selectedTypeSlug,
  );
  const otherClientUuidsForType = getClientUuidsForType(
    typedAllUsersGroupedData?.reliance,
    selectedTypeSlug,
  );

  const filteredClientsOptionsReliance = relianceClientUuidsForType
    ? (clientsOptions ?? []).filter((opt: any) =>
        relianceClientUuidsForType.has(opt.value),
      )
    : clientsOptions;

  const filteredClientsOptionsOther = otherClientUuidsForType
    ? (clientsOptions ?? []).filter((opt: any) =>
        otherClientUuidsForType.has(opt.value),
      )
    : clientsOptions;

  const findUuidInGrouped = (
    data: GroupedInvestmentsData | undefined,
    uuid: string,
  ): boolean => {
    if (!data) return false;
    for (const section of [data.own, data.reliance]) {
      if (!section) continue;
      for (const category of Object.values(section)) {
        if ((category.items ?? []).some((item: any) => item.uuid === uuid))
          return true;
      }
    }
    return false;
  };

  const handleSellAllSuccess = (uuid: string) => {
    setPendingRedirect({
      uuid,
      target: "archive",
      capturedAt: archivedUpdatedAt,
    });
  };

  const handleEditArchiveSuccess = (uuid: string, isOwn: boolean) => {
    setPendingRedirect({ uuid, target: "my", capturedAt: groupedUpdatedAt, isOwn });
  };

  useEffect(() => {
    if (!pendingRedirect) return;

    if (
      pendingRedirect.target === "archive" &&
      !isArchivedFetching &&
      archivedUpdatedAt > pendingRedirect.capturedAt
    ) {
      if (findUuidInGrouped(typedArchivedData, pendingRedirect.uuid)) {
        handleChangeTab("archive");
        setPendingArchiveUuid(pendingRedirect.uuid);
      }
      setPendingRedirect(null);
      return;
    }

    if (
      pendingRedirect.target === "my" &&
      !isGroupedFetching &&
      groupedUpdatedAt > pendingRedirect.capturedAt
    ) {
      if (findUuidInGrouped(typedGroupedData, pendingRedirect.uuid)) {
        handleChangeTab("my");
        setPendingOpenUuid(pendingRedirect.uuid);
        setPendingOpenIsOwn(pendingRedirect.isOwn ?? null);
      }
      setPendingRedirect(null);
    }
  }, [
    pendingRedirect,
    isArchivedFetching,
    archivedUpdatedAt,
    isGroupedFetching,
    groupedUpdatedAt,
    typedArchivedData,
    typedGroupedData,
  ]);

  console.log(tab, "tabsy");

  return (
    <>
      <PageHead
        title="Spółki w portfelu – podgląd inwestycji | App Reliance Pro"
        description="Zobacz wszystkie spółki w swoim portfelu inwestycyjnym. Aktualne dane, historia i wygodny dostęp w jednym miejscu."
      />

      <div className={`companies${tab === "my" ? " companies--tab-my" : ""}`}>
        <InvestmentsTopBar
          tab={tab}
          isAdmin={isAdmin}
          isCompanyInfoOpenModal={isCompanyInfoOpenModal}
          onAssetSelect={setAddAsset}
        />
        <div className="dashboard-content__wrapper">
          <div className="dashboard-content">
            <InvestmentsTabs
              tab={tab}
              userRole={authorizedUser?.role}
              handleChangeTab={handleChangeTab}
              hasArchivedData={
                !isArchivedLoading &&
                !!(
                  typedArchivedData &&
                  (Object.keys(typedArchivedData.own ?? {}).length > 0 ||
                    Object.keys(typedArchivedData.reliance ?? {}).length > 0)
                )
              }
            />

            {((!isGroupedLoading &&
              typedGroupedData &&
              (tab === "my" || isRelianceTab)) ||
              isAdminOtherTab) && (
              <>
                {isDesktop() ? (
                  <DashboardTopPortal>
                    {!(isRelianceTab && !isAdmin) && (
                      <InvestmentsSummary
                        tab={tab}
                        isAdmin={isAdmin}
                        variant={
                          isAdmin && (isRelianceTab || isAdminOtherTab)
                            ? "reliance"
                            : "default"
                        }
                      />
                    )}
                  </DashboardTopPortal>
                ) : (
                  <>
                    {!(isRelianceTab && !isAdmin) && (
                      <InvestmentsSummary
                        tab={tab}
                        isAdmin={isAdmin}
                        variant={
                          isAdmin && (isRelianceTab || isAdminOtherTab)
                            ? "reliance"
                            : "default"
                        }
                      />
                    )}
                  </>
                )}

                {!isDesktop() &&
                  (tab === "my" || (isAdmin && isRelianceTab)) && (
                    <div className="investmentsPage__mobile-actions">
                      <Button
                        onClick={openMeetingBooking}
                        className="button--strokeBlack investment-button"
                        label="Umów spotkanie"
                        svg={SVG_ENUM.CALENDAR}
                      />
                      <AddAssetDropdown
                        onSelect={(slug: AssetCategorySlug) =>
                          setAddAsset(slug)
                        }
                      />
                    </div>
                  )}
              </>
            )}

            {!isGroupedLoading &&
              typedGroupedData &&
              (tab === "my" || isRelianceTab) && (
                <>
                  {!isRelianceTab && (
                    <>
                      {Object.keys(typedGroupedData.own ?? {}).length === 0 &&
                      Object.keys(typedGroupedData.reliance ?? {}).length ===
                        0 ? (
                        <EmptyState
                          className="companies-table__empty"
                          svg={SVG_ENUM.INFO}
                          text={
                            <p>
                              W tym miejscu pojawi się lista Twoich zakupionych
                              aktywów. Aby dodać aktywo zakupione poza Reliance,
                              kliknij przycisk <b>&quot;Dodaj aktywo&quot;</b> w
                              prawym górnym rogu.
                            </p>
                          }
                        />
                      ) : (
                        <>
                          {!isAdmin && !authorizedUser?.package && (
                            <Banner
                              text={
                                <p>
                                  Aby uzyskać dostęp do informacji rynkowych
                                  i&nbsp;powiadomień{" "}
                                  <b>konieczne jest zakupienie pakietu</b>
                                </p>
                              }
                              buttonLabel="Sprawdź dostępne pakiety"
                              buttonOnClick={() =>
                                setModalManage({
                                  type: "packet",
                                  company: null,
                                })
                              }
                              className="companies__top-banner"
                            />
                          )}
                          {typedGroupedData.reliance &&
                            Object.keys(typedGroupedData.reliance).length >
                              0 && (
                              <AssetTilesList
                                categories={typedGroupedData.reliance}
                                title="Aktywa zakupione w Reliance"
                                disableDragAndDrop={isAdmin}
                                disableCollapse={isAdmin}
                                otherSectionCategories={typedGroupedData.own}
                                pendingOpenUuid={
                                  pendingOpenIsOwn === true
                                    ? null
                                    : pendingOpenCurrencyUuid
                                    ? null
                                    : pendingOpenUuid
                                }
                                pendingOpenCurrencyUuid={
                                  pendingOpenCurrencyUuid
                                }
                                onPendingOpenComplete={() => {
                                  setPendingOpenUuid(null);
                                  setPendingOpenCurrencyUuid(null);
                                  setPendingOpenIsOwn(null);
                                }}
                                onSellAllSuccess={handleSellAllSuccess}
                              />
                            )}
                          {typedGroupedData.own &&
                            Object.keys(typedGroupedData.own).length > 0 && (
                              <AssetTilesList
                                categories={typedGroupedData.own}
                                title="Aktywa zakupione poza Reliance"
                                disableDragAndDrop={isAdmin}
                                disableCollapse={isAdmin}
                                banner={
                                  !isAdmin &&
                                  (authorizedUser?.package ===
                                    PACKET_ENUM.BASIC ||
                                    authorizedUser?.package ===
                                      PACKET_ENUM.STANDARD) ? (
                                    <Banner
                                      text={
                                        <p>
                                          <b>Zakup wyższy pakiet</b>, aby
                                          odblokować dostęp do informacji
                                          rynkowych na temat aktywów zakupionych
                                          poza Reliance.
                                        </p>
                                      }
                                      buttonLabel="Zmiana pakietu"
                                      buttonOnClick={() =>
                                        setModalManage({
                                          type: "packet",
                                          company: null,
                                        })
                                      }
                                    />
                                  ) : undefined
                                }
                                isOwn={true}
                                otherSectionCategories={
                                  typedGroupedData.reliance
                                }
                                pendingOpenUuid={
                                  pendingOpenIsOwn === false
                                    ? null
                                    : pendingOpenUuid
                                }
                                pendingOpenCurrencyUuid={
                                  pendingOpenCurrencyUuid
                                }
                                onPendingOpenComplete={() => {
                                  setPendingOpenUuid(null);
                                  setPendingOpenCurrencyUuid(null);
                                  setPendingOpenIsOwn(null);
                                }}
                                onSellAllSuccess={handleSellAllSuccess}
                              />
                            )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            {!isGroupedLoading &&
              typedGroupedData &&
              isRelianceTab &&
              !isAdmin && (
                <>
                  {typedGroupedData.reliance &&
                    Object.keys(typedGroupedData.reliance).length > 0 && (
                      <AssetTilesList
                        categories={typedGroupedData.reliance}
                        title="Aktywa w ofercie Reliance"
                        disableDragAndDrop
                        disableCollapse
                        hideContextMenu
                        showCompanyDetails
                        pendingOpenUuid={
                          pendingOpenCurrencyUuid ? null : pendingOpenUuid
                        }
                        pendingOpenCurrencyUuid={pendingOpenCurrencyUuid}
                        onPendingOpenComplete={() => {
                          setPendingOpenUuid(null);
                          setPendingOpenCurrencyUuid(null);
                        }}
                      />
                    )}
                </>
              )}
            {!isGroupedLoading &&
              typedGroupedData &&
              isRelianceTab &&
              isAdmin && (
                <>
                  <div className="investmentsPage__admin-filter">
                    <InvestmentClientFilter
                      options={
                        filteredClientsOptionsReliance as InvestmentClientOption[]
                      }
                      value={selectedAdminClient}
                      onChange={(opt) => {
                        setSelectedAdminClient(opt);
                      }}
                    />
                  </div>
                  {relianceBadges.length > 0 && (
                    <div className="investmentsPage__admin-type-badges">
                      <button
                        className={`investmentsPage__type-badge${
                          selectedTypeSlug === null
                            ? " investmentsPage__type-badge--active"
                            : ""
                        }`}
                        onClick={() => setSelectedTypeSlug(null)}
                      >
                        Wszystkie
                      </button>
                      {relianceBadges.map((b) => (
                        <button
                          key={b.slug}
                          className={`investmentsPage__type-badge${
                            selectedTypeSlug === b.slug
                              ? " investmentsPage__type-badge--active"
                              : ""
                          }`}
                          onClick={() => {
                            const newSlug =
                              selectedTypeSlug === b.slug ? null : b.slug;
                            setSelectedTypeSlug(newSlug);
                            if (newSlug && selectedAdminClient) {
                              const uuids = getClientUuidsForType(
                                typedGroupedData?.reliance,
                                newSlug,
                              );
                              if (
                                uuids &&
                                !uuids.has(selectedAdminClient.value)
                              ) {
                                setSelectedAdminClient(null);
                              }
                            }
                          }}
                        >
                          {b.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {filteredRelianceCategories &&
                    Object.keys(filteredRelianceCategories).length > 0 && (
                      <AssetTilesList
                        categories={filteredRelianceCategories}
                        disableDragAndDrop={isAdmin}
                        disableCollapse={isAdmin}
                      />
                    )}
                </>
              )}
            {tab === "other" && (
              <>
                {!isAllUsersGroupedLoading &&
                  typedAllUsersGroupedData?.reliance && (
                    <>
                      <div className="investmentsPage__admin-filter">
                        <InvestmentClientFilter
                          options={
                            filteredClientsOptionsOther as InvestmentClientOption[]
                          }
                          value={selectedAdminClient}
                          onChange={(opt) => {
                            setSelectedAdminClient(opt);
                          }}
                        />
                      </div>
                      {otherBadges.length > 0 && (
                        <div className="investmentsPage__admin-type-badges">
                          <button
                            className={`investmentsPage__type-badge${
                              selectedTypeSlug === null
                                ? " investmentsPage__type-badge--active"
                                : ""
                            }`}
                            onClick={() => setSelectedTypeSlug(null)}
                          >
                            Wszystkie
                          </button>
                          {otherBadges.map((b) => (
                            <button
                              key={b.slug}
                              className={`investmentsPage__type-badge${
                                selectedTypeSlug === b.slug
                                  ? " investmentsPage__type-badge--active"
                                  : ""
                              }`}
                              onClick={() => {
                                const newSlug =
                                  selectedTypeSlug === b.slug ? null : b.slug;
                                setSelectedTypeSlug(newSlug);
                                if (newSlug && selectedAdminClient) {
                                  const uuids = getClientUuidsForType(
                                    typedAllUsersGroupedData?.reliance,
                                    newSlug,
                                  );
                                  if (
                                    uuids &&
                                    !uuids.has(selectedAdminClient.value)
                                  ) {
                                    setSelectedAdminClient(null);
                                  }
                                }
                              }}
                            >
                              {b.name}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="investmentsPage__admin-info">
                        <SVG
                          type={SVG_ENUM.INFO}
                          className="investmentsPage__admin-info-icon"
                        />
                        <span className="investmentsPage__admin-info-text">
                          Widoczność szczegółów i transakcji możliwa tylko
                          wtedy, gdy klient udzielił zgody (checkbox podczas
                          dodawania lub edycji aktywa). Aktywa bez zgody są
                          widoczne tylko jako wpis — bez danych.
                        </span>
                      </div>
                      {filteredOtherCategories &&
                        Object.keys(filteredOtherCategories).length > 0 && (
                          <AssetTilesList
                            categories={filteredOtherCategories}
                            disableDragAndDrop={isAdmin}
                            disableCollapse={isAdmin}
                            blurRestrictedData={isAdminOtherTab}
                          />
                        )}
                    </>
                  )}
              </>
            )}
            {tab === "archive" && (
              <>
                {!isArchivedLoading && typedArchivedData && (
                  <>
                    {Object.keys(typedArchivedData.own ?? {}).length === 0 &&
                    Object.keys(typedArchivedData.reliance ?? {}).length ===
                      0 ? (
                      <EmptyState
                        className="companies-table__empty"
                        svg={SVG_ENUM.INFO}
                        text={<p>Brak zarchiwizowanych aktywów.</p>}
                      />
                    ) : (
                      <>
                        {typedArchivedData.reliance &&
                          Object.keys(typedArchivedData.reliance).length >
                            0 && (
                            <AssetTilesList
                              categories={typedArchivedData.reliance}
                              title="Aktywa zakupione w Reliance"
                              disableDragAndDrop={isAdmin}
                              disableCollapse={isAdmin}
                              isArchived={true}
                              hideContextMenu={!isAdmin}
                              otherSectionCategories={typedArchivedData.own}
                              pendingOpenUuid={pendingArchiveUuid}
                              onPendingOpenComplete={() =>
                                setPendingArchiveUuid(null)
                              }
                              onEditArchiveSuccess={handleEditArchiveSuccess}
                            />
                          )}
                        {typedArchivedData.own &&
                          Object.keys(typedArchivedData.own).length > 0 && (
                            <AssetTilesList
                              categories={typedArchivedData.own}
                              title="Aktywa zakupione poza Reliance"
                              disableDragAndDrop={isAdmin}
                              disableCollapse={isAdmin}
                              isOwn={true}
                              isArchived={true}
                              otherSectionCategories={
                                typedArchivedData.reliance
                              }
                              pendingOpenUuid={pendingArchiveUuid}
                              onPendingOpenComplete={() =>
                                setPendingArchiveUuid(null)
                              }
                              onEditArchiveSuccess={handleEditArchiveSuccess}
                            />
                          )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <AddInvestmentModal
        isOpen={
          modalManage.type === "addAsset" &&
          selectedSlug !== "stocks" &&
          selectedSlug !== "shares"
        }
        onClose={closeAddAssetModal}
        selectedSlug={selectedSlug}
        isAdmin={isAdmin}
        mode="add"
        onSuccess={(data) => {
          if (data?.uuid) setPendingOpenUuid(data.uuid);
        }}
      />

      <AddCompanyModal
        isOpen={
          isAdmin &&
          modalManage.type === "addAsset" &&
          (selectedSlug === "stocks" || selectedSlug === "shares")
        }
        onClose={closeAddAssetModal}
        defaultType={
          selectedSlug === "stocks"
            ? "stock"
            : selectedSlug === "shares"
            ? "share"
            : undefined
        }
      />

      <AddCompanyClientModal
        isOpen={
          !isAdmin &&
          modalManage.type === "addAsset" &&
          (selectedSlug === "stocks" || selectedSlug === "shares")
        }
        onClose={closeAddAssetModal}
        defaultType={
          selectedSlug === "stocks"
            ? "stock"
            : selectedSlug === "shares"
            ? "share"
            : undefined
        }
        onSuccess={(data) => {
          const created = data?.["Created Company"];
          if (created?.company?.uuid) {
            setPendingOpenUuid(created.company.uuid);
            setPendingOpenCurrencyUuid(created?.share?.currencyUuid ?? null);
            refetchGrouped();
            queryClient.invalidateQueries({
              queryKey: ["investments/summary"],
            });
          }
        }}
      />

      <TransactionsSideModal
        isOpen={!!transactionCompany}
        onClose={() => setTransactionCompany(null)}
        company={transactionCompany}
        setEditTransaction={(company) => {
          setTransactionCompany(null);
          setTimeout(() => setAddSharesCompany(company), 100);
        }}
        onAddTransactionClick={() => {
          setTransactionCompany(null);
          setTimeout(
            () => setSellSharesCompany(transactionCompanyRef.current),
            100,
          );
        }}
      />

      <SellSharesModal
        isOpen={!!sellSharesCompany}
        onSuccess={returnToTransactionSideModal}
        onClose={returnToTransactionSideModal}
        hardClose={() => setSellSharesCompany(null)}
        company={sellSharesCompany}
      />

      <AddSharesModal
        isOpen={!!addSharesCompany}
        onSuccess={returnToTransactionSideModal}
        onClose={returnToTransactionSideModal}
        hardClose={() => setAddSharesCompany(null)}
        company={addSharesCompany}
      />

      <PacketModal
        isOpen={modalManage.type === "packet"}
        onClose={() => setModalManage({ type: "", company: null })}
        parent="payment"
      />
    </>
  );
};

export default InvestmentsPage;
